import React, { useEffect, useRef, useState, useMemo, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, MoreVertical, PanelLeftClose, PanelLeftOpen, Pencil, Search, SquarePen, X } from "lucide-react";
import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import GlideMenu from "./GlideMenu";
import CustomerWorkspaceMenu, { type CustomerOption } from "./CustomerWorkspaceMenu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { parseUtcDate, toISTDateString } from "../../lib/utils";

export type { CustomerOption };
export type SidebarRecent = { id: string; label: string; prompt?: string; created_at?: string; updated_at?: string; message_count?: number };
export type SidebarNavItem = { key: string; label: string; icon: ReactNode; count?: string; href?: string; onClick?: () => void };
export interface ConversationItem {
  conversation_id: string;
  title?: string | null;
  created_at: string;
  updated_at?: string;
  message_count?: number;
  last_message?: string;
}

export type SidebarNavProps = {
  userType?: string | null;
  customers?: CustomerOption[] | null;
  selectedCustId?: string;
  onSelectCustomer?: (custId: string) => void;
  conversations?: ConversationItem[];
  activeConversationId?: string | null;
  onSelectConversation?: (conversationId: string) => void;
  onDeleteConversation?: (conversationId: string) => Promise<void>;
  onRenameConversation?: (conversationId: string, title: string) => Promise<void>;
  isLoadingConversations?: boolean;
  isOpenMobile?: boolean;
  onCloseMobile?: () => void;
  activeTitle?: string | null;
  className?: string;
  fill?: boolean;
  onNewChat?: () => void;
  onPick?: (id: string, label: string, prompt?: string) => void;
  activeNav?: string;
  onNavigate?: (key: string) => void;
  navItems?: SidebarNavItem[];
  workspaceName?: string;
  footerLabel?: string;
  footerIcon?: ReactNode;
  onFooterClick?: () => void;
  recents?: SidebarRecent[];
  variant?: string;
};

const DEFAULT_WORKSPACE = { key: "mrc", name: "MRC Logistics" };
const DEFAULT_RECENTS: SidebarRecent[] = [
  { id: "shipments", label: "Shipment records" },
  { id: "tracking", label: "Track active shipments" },
  { id: "analytics", label: "Monthly analytics" },
  { id: "lanes", label: "Top delivery lanes" },
];

const SIDEBAR_MOTION = { expandedWidth: 224, collapsedWidth: 52, duration: 280, copyDuration: 180, copyOffset: 8, easing: "cubic-bezier(0.16, 1, 0.3, 1)" };
const CHAT_SEARCH_MOTION = { duration: 180, closedWidth: 28, easing: "cubic-bezier(0.16, 1, 0.3, 1)" };
const CHAT_MENU_WIDTH = 144;
const SIDEBAR_RESIZE = { minWidth: 200, maxWidth: 420, keyboardStep: 16, storageKey: "sidebar-width" };

function clampWidth(width: number): number {
  return Math.min(SIDEBAR_RESIZE.maxWidth, Math.max(SIDEBAR_RESIZE.minWidth, width));
}

function readStoredWidth(): number {
  try {
    const stored = Number(window.localStorage.getItem(SIDEBAR_RESIZE.storageKey));
    return stored ? clampWidth(stored) : SIDEBAR_MOTION.expandedWidth;
  } catch {
    return SIDEBAR_MOTION.expandedWidth;
  }
}

function saveWidth(width: number) {
  try {
    window.localStorage.setItem(SIDEBAR_RESIZE.storageKey, String(width));
  } catch {
    // Storage unavailable: the width just won't persist.
  }
}

type TimeGroup = "Today" | "Yesterday" | "Previous 7 Days" | "Previous 30 Days" | "Older";
const GROUP_ORDER: TimeGroup[] = ["Today", "Yesterday", "Previous 7 Days", "Previous 30 Days", "Older"];

function getTimeGroup(dateStr: string): TimeGroup {
  const date = parseUtcDate(dateStr);
  if (!date) return "Older";
  const now = new Date();
  if (toISTDateString(date) === toISTDateString(now)) return "Today";
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (toISTDateString(date) === toISTDateString(y)) return "Yesterday";
  const days = (now.getTime() - date.getTime()) / 86400000;
  return days < 7 ? "Previous 7 Days" : days < 30 ? "Previous 30 Days" : "Older";
}

function getConversationLabel(conv: ConversationItem): string {
  return conv.title?.trim() || "New chat";
}

const GlideGroup = ({ children }: { children: ReactNode }) => (
  <GlideMenu rowSelector="[data-row]" highlightClassName="sidebar-glide-highlight rounded-[7px] bg-hover-2" className="group/glide flex flex-col gap-px">
    {children}
  </GlideMenu>
);

export default function SidebarNav({
  userType,
  customers = [],
  selectedCustId,
  onSelectCustomer,
  conversations,
  activeConversationId,
  onSelectConversation,
  onDeleteConversation,
  onRenameConversation,
  isLoadingConversations = false,
  isOpenMobile,
  onCloseMobile,
  activeTitle,
  className = "",
  fill = true,
  onNewChat,
  onPick,
  activeNav,
  onNavigate,
  navItems = [],
  workspaceName,
  footerLabel,
  footerIcon,
  onFooterClick,
  recents = DEFAULT_RECENTS,
}: SidebarNavProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [width, setWidth] = useState(readStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStart = useRef({ x: 0, width: 0 });
  const [internalNav, setInternalNav] = useState("chats");
  const currentNav = activeNav ?? internalNav;
  const selectNav = (key: string) => { setInternalNav(key); onNavigate?.(key); };

  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [workspacePos, setWorkspacePos] = useState({ top: 0, left: 0 });
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [menu, setMenu] = useState<{ id: string; label: string; top: number; left: number } | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const renameInputRef = useRef<HTMLInputElement>(null);
  const renameSettled = useRef(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Partial<Record<TimeGroup, boolean>>>({});
  const [chatsCollapsed, setChatsCollapsed] = useState(false);
  // Opening the search always reveals the list so results are never hidden.
  const isChatsCollapsed = chatsCollapsed && !searchOpen;

  const workspaceButtonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const menuPortalRef = useRef<HTMLDivElement | null>(null);
  const chatListRef = useRef<HTMLDivElement | null>(null);

  const activeCustomer = useMemo(() => {
    if (!customers?.length) return null;
    return customers.find((c) => c.cust_code === selectedCustId) || customers[0];
  }, [customers, selectedCustId]);

  // Only internal users can switch customer; customer users just see their own name
  const canSwitchCustomer = userType === "Internal";

  const activeWorkspaceName = activeCustomer ? activeCustomer.cust_name : workspaceName || DEFAULT_WORKSPACE.name;

  const filteredConversations = useMemo(() => {
    if (!conversations) return null;
    if (!query.trim()) return conversations;
    const q = query.toLowerCase();
    return conversations.filter((c) => getConversationLabel(c).toLowerCase().includes(q) || (c.last_message && c.last_message.toLowerCase().includes(q)));
  }, [conversations, query]);

  const groupedConversations = useMemo(() => {
    if (!filteredConversations) return null;
    const map: Partial<Record<TimeGroup, ConversationItem[]>> = {};
    for (const conv of filteredConversations) {
      const g = getTimeGroup(conv.updated_at || conv.created_at);
      (map[g] = map[g] || []).push(conv);
    }
    return map;
  }, [filteredConversations]);

  const visibleRecents = useMemo(() => {
    if (conversations) return [];
    if (!query.trim()) return recents;
    return recents.filter((item) => item.label.toLowerCase().includes(query.trim().toLowerCase()));
  }, [conversations, recents, query]);

  useEffect(() => {
    if (!workspaceOpen) return;
    const close = (e: PointerEvent) => {
      const el = e.target as Element;
      if (!el.closest("[data-workspace-trigger]") && !el.closest("[data-workspace-menu]")) setWorkspaceOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [workspaceOpen]);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (!menu) return;
    const close = (e: PointerEvent) => {
      const el = e.target as Element;
      if (!el.closest("[data-chat-menu]") && !el.closest("[data-chat-menu-trigger]")) setMenu(null);
    };
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenu(null);
        menuTriggerRef.current?.focus();
      }
    };
    const closeOnScroll = (e: Event) => {
      const target = e.target;
      if (target === window || target === document || target === chatListRef.current || (target instanceof Node && chatListRef.current?.contains(target))) {
        setMenu(null);
      }
    };
    const closeOnResize = () => setMenu(null);

    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", closeOnResize);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", closeOnResize);
    };
  }, [menu]);

  useEffect(() => {
    if (menu) {
      const firstItem = menuPortalRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]');
      firstItem?.focus();
    }
  }, [menu]);

  useEffect(() => {
    if (!renamingId) return;
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
  }, [renamingId]);

  const handleConfirmDelete = async () => {
    if (!deletingId || !onDeleteConversation) return;
    try {
      setIsDeleting(true);
      await onDeleteConversation(deletingId);
    } finally {
      setIsDeleting(false);
      setDeletingId(null);
    }
  };

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, id: string, label: string) => {
    if (menu?.id === id) {
      setMenu(null);
      return;
    }
    menuTriggerRef.current = e.currentTarget;
    const rect = e.currentTarget.getBoundingClientRect();
    setMenu({ id, label, top: rect.bottom + 4, left: Math.max(8, rect.right - CHAT_MENU_WIDTH) });
  };

  const startRename = (id: string, label: string) => {
    setMenu(null);
    renameSettled.current = false;
    setRenameValue(label);
    setRenamingId(id);
  };

  const cancelRename = () => {
    renameSettled.current = true;
    setRenamingId(null);
  };

  const commitRename = async () => {
    if (renameSettled.current || !renamingId) return;
    renameSettled.current = true;
    const id = renamingId;
    const next = renameValue.trim();
    const original = conversations?.find((c) => c.conversation_id === id);
    setRenamingId(null);
    if (!next || (original && next === getConversationLabel(original))) return;
    await onRenameConversation?.(id, next);
  };

  const startResize = (e: React.PointerEvent<HTMLDivElement>) => {
    setMenu(null);
    e.currentTarget.setPointerCapture(e.pointerId);
    resizeStart.current = { x: e.clientX, width };
    setIsResizing(true);
  };

  const moveResize = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isResizing) return;
    setWidth(clampWidth(resizeStart.current.width + e.clientX - resizeStart.current.x));
  };

  const endResize = () => {
    if (!isResizing) return;
    setIsResizing(false);
    saveWidth(width);
  };

  const resetWidth = () => {
    setWidth(SIDEBAR_MOTION.expandedWidth);
    saveWidth(SIDEBAR_MOTION.expandedWidth);
  };

  // Rows fill the sidebar (minus their 8px side margins) while expanded; collapsed width comes from CSS.
  const rowStyle: CSSProperties = {
    ...(collapsed ? {} : { width: "calc(100% - 1rem)" }),
    ...(isResizing ? { transitionDuration: "0ms" } : {}),
  };

  const keyResize = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const delta = e.key === "ArrowRight" ? SIDEBAR_RESIZE.keyboardStep : e.key === "ArrowLeft" ? -SIDEBAR_RESIZE.keyboardStep : 0;
    if (!delta) return;
    e.preventDefault();
    const next = clampWidth(width + delta);
    setWidth(next);
    saveWidth(next);
  };

  const renderRow = (id: string, label: string, isActive: boolean) => (
    <div
      key={id}
      data-row
      role="button"
      tabIndex={collapsed ? -1 : 0}
      aria-label={`Open conversation: ${label}`}
      aria-hidden={collapsed}
      onClick={() => {
        selectNav("chats");
        if (conversations) onSelectConversation?.(id);
        else onPick?.(id, label);
        onCloseMobile?.();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          selectNav("chats");
          if (conversations) onSelectConversation?.(id);
          else onPick?.(id, label);
          onCloseMobile?.();
        }
      }}
      className={`sidebar-row group/chat-row relative z-10 mx-2 flex h-8 items-center justify-between rounded-[8px] px-2 text-left cursor-pointer transition-[width,background-color,color,transform] duration-150 active:scale-[0.98] outline-none focus-visible:ring-1 focus-visible:ring-indigo-500 ${
        isActive ? "bg-hover-2 group-hover/glide:bg-transparent" : ""
      } ${collapsed ? "pointer-events-none select-none invisible" : ""}`}
      style={rowStyle}
    >
      {renamingId === id ? (
        <input
          ref={renameInputRef}
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onBlur={commitRename}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") cancelRename();
          }}
          aria-label="Rename chat"
          maxLength={80}
          className="sidebar-copy h-6 min-w-0 flex-1 rounded-[6px] bg-field px-1.5 text-[13px] font-medium text-ink shadow-hairline outline-none"
        />
      ) : (
        <span className={`sidebar-copy min-w-0 flex-1 truncate text-[13px] font-medium ${isActive ? "text-ink font-semibold" : "text-ink-2"}`}>
          {label}
        </span>
      )}
      {conversations && renamingId !== id && (onRenameConversation || onDeleteConversation) && (
        <button
          type="button"
          data-chat-menu-trigger
          aria-label="Chat options"
          aria-haspopup="menu"
          aria-expanded={menu?.id === id}
          onClick={(e) => { e.stopPropagation(); openMenu(e, id, label); }}
          className={`sidebar-copy flex size-6 shrink-0 items-center justify-center rounded-[6px] text-ink-3 transition-opacity hover:bg-line-strong hover:text-ink focus-visible:opacity-100 ${
            menu?.id === id ? "opacity-100" : "max-md:opacity-100 md:opacity-0 md:group-hover/chat-row:opacity-100"
          }`}
        >
          <MoreVertical size={16} />
        </button>
      )}
    </div>
  );

  return (
    <>
      {isOpenMobile && <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-xs md:hidden" onClick={onCloseMobile} />}

      <aside
        data-sidebar-collapsed={collapsed}
        aria-label="Workspace navigation"
        className={`relative z-40 flex shrink-0 overflow-hidden bg-surface border-r border-line max-w-[85vw] md:max-w-none transition-[width,transform] ${
          fill ? "h-full" : "h-[600px]"
        } ${
          isOpenMobile !== undefined
            ? isOpenMobile ? "fixed inset-y-0 left-0 translate-x-0 md:static" : "fixed inset-y-0 left-0 -translate-x-full md:translate-x-0 md:static"
            : ""
        } ${isResizing ? "select-none" : ""} ${className}`}
        style={{
          width: collapsed ? SIDEBAR_MOTION.collapsedWidth : width,
          transitionDuration: isResizing ? "0ms" : `${SIDEBAR_MOTION.duration}ms`,
          transitionTimingFunction: SIDEBAR_MOTION.easing,
          "--sidebar-copy-duration": `${SIDEBAR_MOTION.copyDuration}ms`,
          "--sidebar-copy-offset": `${SIDEBAR_MOTION.copyOffset}px`,
          "--sidebar-easing": SIDEBAR_MOTION.easing,
        } as CSSProperties}
      >
        <div
          className="flex min-h-0 shrink-0 flex-col max-w-[85vw] md:max-w-none"
          style={{
            width,
            paddingLeft: collapsed ? 0 : 8,
            transition: `padding-left ${SIDEBAR_MOTION.duration}ms ${SIDEBAR_MOTION.easing}`,
          }}
        >
          {/* Header & Workspace Switcher */}
          <div className="relative mb-2.5 h-10 shrink-0">
            {canSwitchCustomer ? (
            <button
              ref={workspaceButtonRef}
              data-workspace-trigger
              type="button"
              aria-expanded={workspaceOpen}
              aria-hidden={collapsed}
              tabIndex={collapsed ? -1 : 0}
              onClick={() => {
                if (!workspaceOpen && workspaceButtonRef.current) {
                  const rect = workspaceButtonRef.current.getBoundingClientRect();
                  setWorkspacePos({ top: rect.bottom + 6, left: rect.left });
                }
                setWorkspaceOpen((o) => !o);
              }}
              className="sidebar-workspace-control absolute left-2 right-[52px] top-1 flex h-8 items-center rounded-[8px] px-2 text-left transition-[background-color,transform] duration-100 hover:bg-hover-2 active:scale-[0.99]"
            >
              <span className="sidebar-copy min-w-0 flex-1 truncate text-[13.5px] font-medium text-ink-2">{activeWorkspaceName}</span>
              <span className="sidebar-copy ml-1 flex shrink-0 text-ink-3"><ChevronDown size={16} /></span>
            </button>
            ) : (
              // Customer and demo users have one customer, so New chat takes the header slot
              <button
                type="button"
                aria-hidden={collapsed}
                tabIndex={collapsed ? -1 : 0}
                onClick={() => { selectNav("chats"); onNewChat?.(); onCloseMobile?.(); }}
                className="sidebar-workspace-control absolute left-2 right-[52px] top-1 flex h-8 items-center rounded-[8px] px-2 text-left transition-[background-color,transform] duration-100 hover:bg-hover-2 active:scale-[0.99]"
              >
                <span className="flex size-5 shrink-0 items-center justify-center text-ink"><SquarePen size={18} /></span>
                <span className="sidebar-copy ml-1.5 min-w-0 flex-1 truncate text-[14px] font-medium text-ink">New chat</span>
              </button>
            )}

            {canSwitchCustomer && workspaceOpen && (
              <CustomerWorkspaceMenu
                position={workspacePos}
                onClose={() => setWorkspaceOpen(false)}
                customers={customers ?? undefined}
                selectedCustId={selectedCustId}
                onSelectCustomer={onSelectCustomer}
                userType={userType}
                currentName={activeWorkspaceName}
              />
            )}

            <button
              type="button"
              aria-label="Collapse sidebar"
              aria-hidden={collapsed}
              tabIndex={collapsed ? -1 : 0}
              onClick={() => { setCollapsed(true); setWorkspaceOpen(false); setSearchOpen(false); setQuery(""); }}
              className="sidebar-collapse-control absolute right-2 top-1 flex size-8 items-center justify-center rounded-[8px] text-ink-3 transition-[opacity,background-color,color] duration-150 hover:bg-hover-2 hover:text-ink"
            >
              <PanelLeftClose size={18} />
            </button>
            <button
              type="button"
              aria-label="Expand sidebar"
              aria-hidden={!collapsed}
              tabIndex={collapsed ? 0 : -1}
              onClick={() => setCollapsed(false)}
              className="sidebar-expand-control absolute left-2 top-0.5 flex size-9 items-center justify-center rounded-[8px] text-ink-3 transition-[opacity,background-color,color] duration-150 hover:bg-hover-2 hover:text-ink"
            >
              <PanelLeftOpen size={18} />
            </button>
            {onCloseMobile && (
              <button
                type="button"
                aria-label="Close sidebar"
                onClick={onCloseMobile}
                className="md:hidden absolute right-2 top-1 flex size-8 items-center justify-center rounded-[8px] text-ink-3 hover:bg-hover-2 hover:text-ink"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* New Chat & Custom Nav Actions (Home tab removed) */}
          <GlideGroup>
            {(canSwitchCustomer || collapsed) && (
            <button
              data-row
              type="button"
              onClick={() => { selectNav("chats"); onNewChat?.(); onCloseMobile?.(); }}
              className="sidebar-row relative z-10 mx-2 flex h-8 items-center rounded-[8px] px-2 text-left transition-[width,background-color,color,transform] duration-150 active:scale-[0.98]"
              style={rowStyle}
            >
              <span className="flex size-5 shrink-0 items-center justify-center text-ink"><SquarePen size={18} /></span>
              <span className="sidebar-copy ml-1.5 min-w-0 flex-1 truncate text-[14px] font-medium text-ink">New chat</span>
            </button>
            )}
            {navItems.map((item) => (
              <button
                key={item.key}
                data-row
                type="button"
                onClick={() => { selectNav(item.key); item.onClick?.(); }}
                className={`sidebar-row relative z-10 mx-2 flex h-8 items-center rounded-[8px] px-2 text-left transition-[width,background-color,color,transform] duration-150 active:scale-[0.98] ${
                  currentNav === item.key ? "bg-hover-2 group-hover/glide:bg-transparent" : ""
                }`}
                style={rowStyle}
              >
                <span className={`flex size-5 shrink-0 items-center justify-center ${currentNav === item.key ? "text-ink" : "text-ink-2"}`}>{item.icon}</span>
                <span className={`sidebar-copy ml-1.5 min-w-0 flex-1 truncate text-[14px] font-medium ${currentNav === item.key ? "text-ink" : "text-ink-2"}`}>{item.label}</span>
                {item.count && <span className="sidebar-copy mr-2 shrink-0 text-[12px] font-medium tabular-nums text-ink-3">{item.count}</span>}
              </button>
            ))}
          </GlideGroup>

          {/* Chats Header & Search Input */}
          <div ref={chatListRef} className="mt-3 min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            <div className="sidebar-copy relative mx-2 mb-1 h-8">
              <button
                type="button"
                aria-expanded={!isChatsCollapsed}
                aria-hidden={searchOpen}
                tabIndex={searchOpen ? -1 : 0}
                onClick={() => setChatsCollapsed((o) => !o)}
                className={`absolute inset-0 flex items-center gap-1.5 px-2 text-left text-[12.5px] font-medium text-ink-3 transition-[opacity,transform,color] hover:text-ink-2 ${
                  searchOpen ? "pointer-events-none -translate-x-1 opacity-0" : "translate-x-0 opacity-100"
                }`}
                style={{ transitionDuration: `${CHAT_SEARCH_MOTION.duration}ms`, transitionTimingFunction: CHAT_SEARCH_MOTION.easing }}
              >
                <ChevronDown size={16} className={`shrink-0 transition-transform duration-150 ${isChatsCollapsed ? "-rotate-90" : ""}`} />
                <span>Chats</span>
              </button>

              <button
                type="button"
                aria-label="Search chats"
                aria-expanded={searchOpen}
                onClick={() => setSearchOpen(true)}
                className={`absolute right-0 top-0 z-10 flex size-8 items-center justify-center rounded-[8px] text-ink-3 transition-[opacity,background-color,color,transform] hover:bg-hover-2 hover:text-ink active:scale-[0.96] ${
                  searchOpen ? "pointer-events-none opacity-0" : "opacity-100"
                }`}
                style={{ transitionDuration: `${CHAT_SEARCH_MOTION.duration}ms` }}
              >
                <Search size={16} />
              </button>

              <div
                className={`absolute right-0 top-0 z-20 flex h-8 items-center overflow-hidden rounded-[8px] bg-field text-ink-3 shadow-hairline transition-[width,opacity] focus-within:text-ink-2 ${
                  searchOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
                }`}
                style={{
                  width: searchOpen ? "100%" : CHAT_SEARCH_MOTION.closedWidth,
                  transitionDuration: `${CHAT_SEARCH_MOTION.duration}ms`,
                  transitionTimingFunction: CHAT_SEARCH_MOTION.easing,
                }}
              >
                <span className="ml-2 flex shrink-0 items-center justify-center"><Search size={15} /></span>
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") { setSearchOpen(false); setQuery(""); }
                  }}
                  placeholder="Search chats"
                  aria-label="Search chat history"
                  className="ml-1.5 min-w-0 flex-1 bg-transparent text-[13px] font-medium text-ink outline-none placeholder:text-ink-3"
                />
                <button
                  type="button"
                  aria-label="Close chat search"
                  onClick={() => { setSearchOpen(false); setQuery(""); }}
                  className="flex size-8 shrink-0 items-center justify-center rounded-[8px] text-ink-3 hover:bg-hover-2 hover:text-ink"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Conversation / Recents List */}
            {isChatsCollapsed ? null : isLoadingConversations ? (
              <div className="flex flex-col gap-1.5 px-2 pt-2">
                {[1, 2, 3, 4, 5].map((n) => <div key={n} className="h-8 bg-field/80 rounded-[8px] animate-pulse" />)}
              </div>
            ) : conversations ? (
              <div className="py-1">
                {GROUP_ORDER.map((group) => {
                  const items = groupedConversations?.[group];
                  if (!items?.length) return null;
                  // Searching always shows matches, even inside a minimized group.
                  const isGroupCollapsed = Boolean(collapsedGroups[group]) && !query.trim();
                  const hasActiveInGroup = Boolean(items.some((conv) => conv.conversation_id === activeConversationId));
                  return (
                    <div key={group} className="mb-2">
                      <button
                        type="button"
                        aria-expanded={!isGroupCollapsed}
                        onClick={() => setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }))}
                        className="sidebar-copy flex w-full items-center gap-1 px-3 py-1 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-3 transition-colors hover:text-ink-2"
                      >
                        <ChevronDown size={12} className={`shrink-0 transition-transform duration-150 ${isGroupCollapsed ? "-rotate-90" : ""}`} />
                        <span>{group}</span>
                      </button>
                      {!isGroupCollapsed ? (
                        <GlideGroup>
                          {items.map((conv) => renderRow(conv.conversation_id, getConversationLabel(conv), conv.conversation_id === activeConversationId))}
                        </GlideGroup>
                      ) : hasActiveInGroup ? (
                        <GlideGroup>
                          {items
                            .filter((conv) => conv.conversation_id === activeConversationId)
                            .map((conv) => renderRow(conv.conversation_id, getConversationLabel(conv), true))}
                        </GlideGroup>
                      ) : null}
                    </div>
                  );
                })}
                {filteredConversations?.length === 0 && (
                  <div className="sidebar-copy mx-2 px-2 py-4 text-center text-[12.5px] text-ink-3">
                    {query ? "No chats found" : "No conversations yet"}
                  </div>
                )}
              </div>
            ) : (
              <GlideGroup>
                {visibleRecents.map((item) => renderRow(item.id, item.label, item.label === activeTitle))}
                {query && visibleRecents.length === 0 && (
                  <div className="sidebar-copy mx-2 px-2 py-2 text-[12.5px] text-ink-3">No chats found</div>
                )}
              </GlideGroup>
            )}
          </div>

          {/* Optional Footer CTA */}
          {footerLabel && (
            <div className="sidebar-copy mx-2 mt-3 border-t border-line pt-3 pb-3">
              <button
                type="button"
                onClick={onFooterClick ?? onNewChat}
                className="flex h-8 w-full items-center justify-center gap-1.5 rounded-control bg-hover-2 text-[12.5px] font-medium text-ink transition-[background-color,transform] duration-150 hover:bg-line-strong active:scale-[0.98]"
              >
                {footerIcon}
                {footerLabel}
              </button>
            </div>
          )}
        </div>

        {!collapsed && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
            aria-valuemin={SIDEBAR_RESIZE.minWidth}
            aria-valuemax={SIDEBAR_RESIZE.maxWidth}
            aria-valuenow={width}
            tabIndex={0}
            onPointerDown={startResize}
            onPointerMove={moveResize}
            onPointerUp={endResize}
            onPointerCancel={endResize}
            onDoubleClick={resetWidth}
            onKeyDown={keyResize}
            className={`absolute inset-y-0 right-0 z-30 hidden w-1.5 cursor-col-resize touch-none transition-colors md:block ${
              isResizing ? "bg-line-strong" : "hover:bg-line-strong focus-visible:bg-line-strong"
            } outline-none`}
          />
        )}
      </aside>

      {/* Per-chat options menu (portal, so the sidebar's overflow-hidden can't clip it) */}
      {menu && createPortal(
        <div
          ref={menuPortalRef}
          data-chat-menu
          role="menu"
          tabIndex={-1}
          aria-label="Chat options"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              setMenu(null);
              menuTriggerRef.current?.focus();
            } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              const items = Array.from(menuPortalRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || []);
              if (!items.length) return;
              const currentIdx = items.indexOf(document.activeElement as HTMLButtonElement);
              const nextIdx = e.key === "ArrowDown"
                ? (currentIdx + 1) % items.length
                : (currentIdx - 1 + items.length) % items.length;
              items[nextIdx]?.focus();
            } else if (e.key === "Tab") {
              setMenu(null);
            }
          }}
          className="fixed z-[60] rounded-[10px] border border-line bg-surface p-1 shadow-lg outline-none"
          style={{ top: menu.top, left: menu.left, width: CHAT_MENU_WIDTH }}
        >
          {onRenameConversation && (
            <button
              type="button"
              role="menuitem"
              onClick={() => startRename(menu.id, menu.label)}
              className="flex h-8 w-full items-center gap-2 rounded-[7px] px-2 text-left text-[13px] font-medium text-ink-2 hover:bg-hover-2 hover:text-ink focus-visible:bg-hover-2 focus-visible:text-ink outline-none"
            >
              <Pencil size={14} />
              Rename
            </button>
          )}
          {onDeleteConversation && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { setDeletingId(menu.id); setMenu(null); }}
              className="flex h-8 w-full items-center gap-2 rounded-[7px] px-2 text-left text-[13px] font-medium text-rose-600 hover:bg-hover-2 focus-visible:bg-hover-2 outline-none"
            >
              <TrashIcon className="size-3.5" />
              Delete
            </button>
          )}
        </div>,
        document.body
      )}

      {/* Delete Confirmation Alert */}
      <AlertDialog open={Boolean(deletingId)} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete chat?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this conversation? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleConfirmDelete(); }}
              disabled={isDeleting}
              className="bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-600"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
